import { useEffect, useRef, useState } from 'react';

const nf = new Intl.NumberFormat('it-IT');
const shortDay = (d: string) =>
  new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
    new Date(`${d}T12:00:00Z`),
  );

/** spazio sopra la barra più alta per il numero fisso */
const LABEL_SPACE = '14px';
/** larghezza di un carattere del numero fisso (10px, cifre tabulari) */
const CHAR_PX = 5.8;

/**
 * Barre per giorno (o per ora, con `formatLabel`), con un segno per il secondo valore. I numeri:
 * sopra ogni barra quando c'è spazio (dipende dalla larghezza del grafico e dalla lunghezza dei
 * numeri), e sempre nella riga sopra il grafico passando col mouse o toccando una barra.
 */
export function Bars({
  data,
  format = (n: number) => nf.format(n),
  formatLabel = shortDay,
  legend,
  valueLabel = '',
  extraLabel = 'sessioni',
  className = 'h-40',
}: {
  data: Array<{ label: string; value: number; extra?: number }>;
  format?: (n: number) => string;
  /** etichetta sotto le barre e nella riga dei numeri (di base: la data) */
  formatLabel?: (label: string) => string;
  legend: string;
  /** cosa misura la barra, nella riga dei numeri (es. "pagine viste") */
  valueLabel?: string;
  /** cosa misura il segno sopra la barra */
  extraLabel?: string;
  className?: string;
}) {
  const max = Math.max(...data.map((d) => Math.max(d.value, d.extra ?? 0))) || 1;
  const every = Math.ceil(data.length / 8);
  const [active, setActive] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(e!.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => setActive(null), [data.length]);

  const longest = Math.max(...data.map((d) => (d.value ? format(d.value).length : 0)));
  const fixed = width > 0 && longest * CHAR_PX + 3 <= width / Math.max(1, data.length);
  const cur = active !== null ? data[active] : undefined;
  const readout = (d: (typeof data)[number]) =>
    `${formatLabel(d.label)}: ${format(d.value)}${valueLabel ? ` ${valueLabel}` : ''}${
      d.extra !== undefined ? ` · ${nf.format(d.extra)} ${extraLabel}` : ''
    }`;

  return (
    <figure>
      <p className="mb-1 min-h-5 text-sm tabular-nums" aria-live="polite">
        {cur ? (
          <strong className="font-medium">{readout(cur)}</strong>
        ) : (
          <span className="text-muted">Passa sopra o tocca una barra per i numeri</span>
        )}
      </p>
      <div
        ref={box}
        className={`flex w-full items-stretch ${className}`}
        onPointerLeave={(e) => e.pointerType === 'mouse' && setActive(null)}
      >
        {data.map((d, i) => (
          <button
            key={d.label}
            type="button"
            aria-label={readout(d)}
            onPointerEnter={(e) => e.pointerType === 'mouse' && setActive(i)}
            onClick={() => setActive(i)}
            onFocus={() => setActive(i)}
            className="relative h-full min-w-0 flex-1 outline-none focus-visible:bg-surface"
          >
            {fixed && d.value > 0 && (
              <span
                className="absolute inset-x-0 text-center text-[10px] leading-none text-muted tabular-nums"
                style={{ bottom: `calc((100% - ${LABEL_SPACE}) * ${d.value / max} + 2px)` }}
              >
                {format(d.value)}
              </span>
            )}
            <span
              className={`absolute inset-x-[15%] bottom-0 bg-fixed-yellow transition-opacity ${
                active !== null && active !== i ? 'opacity-50' : ''
              }`}
              style={{ height: `calc((100% - ${LABEL_SPACE}) * ${d.value / max})` }}
            />
            {d.extra !== undefined && d.extra > 0 && (
              <span
                className="absolute inset-x-[15%] h-[2px] bg-fixed-black"
                style={{ bottom: `calc((100% - ${LABEL_SPACE}) * ${d.extra / max})` }}
              />
            )}
          </button>
        ))}
      </div>
      {/* etichette sotto la loro barra, una ogni `every` */}
      <div className="mt-1 flex text-[11px] text-muted" aria-hidden>
        {data.map((d, i) => (
          <span key={d.label} className="flex min-w-0 flex-1 justify-center whitespace-nowrap">
            {i % every === 0 ? formatLabel(d.label) : ''}
          </span>
        ))}
      </div>
      <figcaption className="mt-1 text-xs text-muted">
        Massimo {format(max)} · {legend}
      </figcaption>
    </figure>
  );
}
